import { type ComponentProps, forwardRef, useCallback } from 'react';

import { useTrackRecentlyDeletedSortApplied } from '@air/analytics';
import { SortDirection } from '@air/api/types';

import { RecentlyDeletedTableCell } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableCell';
import { RecentlyDeletedTableHeaderCellActions } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableHeaderCellActions';
import { useRecentlyDeletedQueryParam } from '~/components/RecentlyDeleted/hooks/useRecentlyDeletedQueryParam';
import { getTableColumnWidth } from '~/components/RecentlyDeleted/utils/getTableColumnWidth';
import { SortButton } from '~/components/SortButton/SortButton';

export type RecentlyDeletedTableHeaderProps = Omit<ComponentProps<'div'>, 'children'>;

export const RecentlyDeletedTableHeader = forwardRef<HTMLDivElement, RecentlyDeletedTableHeaderProps>(
  (props, forwardedRef) => {
    const { setSort, sortDirection } = useRecentlyDeletedQueryParam();
    const { trackRecentlyDeletedSortApplied } = useTrackRecentlyDeletedSortApplied();

    const handleClick = useCallback(() => {
      setSort('createdAt', sortDirection === SortDirection.asc ? SortDirection.desc : SortDirection.asc);

      trackRecentlyDeletedSortApplied({
        sort_type: 'date_deleted',
        sort_direction: sortDirection === SortDirection.asc ? SortDirection.desc : SortDirection.asc,
      });
    }, [setSort, sortDirection, trackRecentlyDeletedSortApplied]);

    return (
      <div
        className="sticky top-0 z-1 mb-1 min-h-12 overflow-hidden border-b border-b-gray-4 bg-gray-1 pr-4 pl-4 font-semibold text-12 text-gray-11"
        ref={forwardedRef}
        {...props}
      >
        <div className="flex min-h-[48px] items-center">
          <RecentlyDeletedTableCell width={getTableColumnWidth('title')}>Name</RecentlyDeletedTableCell>
          <RecentlyDeletedTableCell width={getTableColumnWidth('type')}>Type</RecentlyDeletedTableCell>
          <RecentlyDeletedTableCell width={getTableColumnWidth('dateDeleted')}>
            <SortButton isActive={true} direction={sortDirection} onClick={handleClick} type={'Date deleted'}>
              Date deleted
            </SortButton>
          </RecentlyDeletedTableCell>
          <RecentlyDeletedTableCell width={getTableColumnWidth('deletedBy')}>Deleted by</RecentlyDeletedTableCell>
          <RecentlyDeletedTableCell width={getTableColumnWidth('deletedFrom')}>Deleted from</RecentlyDeletedTableCell>
          <RecentlyDeletedTableCell width={getTableColumnWidth('daysToRecover')}>
            Days left to recover
          </RecentlyDeletedTableCell>
          <RecentlyDeletedTableHeaderCellActions />
        </div>
      </div>
    );
  },
);

RecentlyDeletedTableHeader.displayName = 'RecentlyDeletedTableHeader';
